import {Duration} from 'luxon';
import {ClientType} from 'common/src/modules/clients/clients.types';

interface Clients {
  [id: number]: UserClient;
}

interface RemoteUser {
  userClients: RemoteUserClient[];
  email: string;
  firstName: string;
  id: number;
  isActive: boolean;
  lastName: string;
  masterAdminLevel: number;
  roles: string; //"ClinchAdmin",
  lastSeenReleaseNoteVersion: number;
  profilePictureUrl: string;
  isSSOUser: boolean;
}

interface User extends Omit<RemoteUser, 'roles' | 'userClients'> {
  userRoles: RoleTypeName[];
  relatedClients: UserClient[];
  userClients: Clients;
}

interface RemoteSlimUser {
  id: number;
  firstName: string;
  lastName: string;
  profilePictureUrl: string;
  email: string;
}

type SlimUser = RemoteSlimUser;

type RemoteSlimUserWithRoles = RemoteSlimUser & {roles: RoleTypeName[]};

type SlimUserWithRoles = SlimUser & {roles: RoleTypeName[]};

type ConnectedAdvertiser = {
  id: number;
  name: string;
};

interface RemoteUserClient {
  id: number;
  name: string;
  ///Roles that current ACTIVE user holds for the client
  userRoles: RoleTypeName[];
  clientType: ClientType;
  connectedAdvertisers: ConnectedAdvertiser[];
  expiration: string;
}

interface UserClient extends Omit<RemoteUserClient, 'expiration'> {
  expiresIn: Duration;
}

interface AdminUserProfile {
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
}

interface ClientWithSupportedProducts {
  id: number;
  name: string;
  allSupportedProducts: number[];
}

interface RemoteMentionedUser {
  userId: number;
  userLastName: string;
  userFirstName: string;
  userEmail: string;
}

interface MentionedUser {
  id: number;
  lastName: string;
  firstName: string;
  email: string;
}

interface RemoteUpdateUserPayload {
  email: string;
  userId: number;
  agencyId: string;
  lastName: string;
  firstName: string;
  isActive: boolean;
  agencyRoles: string;
  userRoles: string;
  associatedRoles: string;
  associateUsertoClientId: string;
}

interface UpdateUserPayload
  extends Omit<
    RemoteUpdateUserPayload,
    'agencyRoles' | 'userRoles' | 'associatedRoles' | 'associateUsertoClientId'
  > {
  agencyRoles: RoleTypeName[];
  userRoles: RoleTypeName[];
  associatedRoles: RoleTypeName[];
  associateUserToClientId: string;
}

enum RoleType {
  None = 2 ** 0,
  GeneralRead = 2 ** 1,
  AdminWrite = 2 ** 2,
  CampaignsWrite = 2 ** 4,
  AnalyticsWrite = 2 ** 6,
  CreativesWrite = 2 ** 8,
  CreativeTemplatesWrite = 2 ** 9,
  FeedsWrite = 2 ** 10,
  ClinchDev = 2 ** 23,
  ClinchAdmin = 2 ** 25,
}

enum UserType {
  Admin = 'clinch-admin',
  Dev = 'clinch-dev',
  Agency = 'agency',
}

enum RoleTypeName {
  None = 'None',
  GeneralRead = 'GeneralRead',
  AdminWrite = 'AdminWrite',
  CampaignsWrite = 'CampaignsWrite',
  AnalyticsWrite = 'AnalyticsWrite',
  CreativesWrite = 'CreativesWrite',
  CreativeTemplatesWrite = 'CreativeTemplatesWrite',
  FeedsWrite = 'FeedsWrite',
  ClinchDev = 'ClinchDev',
  ClinchAdmin = 'ClinchAdmin',
  ClinchSupport = 'ClinchSupport',
  Billing = 'BillingWrite',
  AdvertiserFeatureManager = 'AdvertiserFeatureManagerWrite',
}

type UpdateUserRolesPayload = {
  userId: number;
  email: string;
  roles: RoleTypeName[];
};

type UpdateClientRolesParams = {
  clientId: number;
  accountId: number;
  updatedUserRoles: UpdateUserRolesPayload[];
  addedUserRoles: UpdateUserRolesPayload[];
  deletedUserIds: number[];
};

export {
  User,
  Clients,
  RoleType,
  UserType,
  RemoteUser,
  RoleTypeName,
  UpdateUserPayload,
  RemoteUpdateUserPayload,
  MentionedUser,
  RemoteMentionedUser,
  RemoteUserClient,
  UserClient,
  ClientWithSupportedProducts,
  AdminUserProfile,
  RemoteSlimUser,
  SlimUser,
  RemoteSlimUserWithRoles,
  UpdateClientRolesParams,
  SlimUserWithRoles,
  UpdateUserRolesPayload,
};
